import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './lib/api';

interface CashForecastProps {
  entrepriseId: string;
}

const formatCurrency = (centimes: number) => {
  const euros = centimes / 100;
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(euros);
};

const CashForecast: React.FC<CashForecastProps> = ({ entrepriseId }) => {
  const [months, setMonths] = useState(3);

  const { data = [], isLoading, error } = useQuery({
    queryKey: ['cashflow', entrepriseId, months],
    queryFn: () => api.cashflow.getForecast(entrepriseId, months),
  });

  if (isLoading) return <div className="text-sm text-gray-500">Chargement...</div>;
  if (error) return <div className="text-red-700 bg-red-50 border border-red-200 px-4 py-3 rounded-lg text-sm mb-4">Erreur: {(error as Error).message}</div>;

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Plan de trésorerie</h2>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">Mois à prévoir:</label>
        <select value={months} onChange={e => setMonths(Number(e.target.value))} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
          <option value={3}>3</option>
          <option value={6}>6</option>
          <option value={12}>12</option>
        </select>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Mois</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Solde initial</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Entrées</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Sorties</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Solde final</th>
            </tr>
          </thead>
          <tbody>
            {data.map((d: any) => (
              <tr key={d.month} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3 text-gray-700 border-b border-gray-100 font-medium">{d.month}</td>
                <td className="px-4 py-3 text-gray-700 border-b border-gray-100 text-right">{formatCurrency(d.opening_balance_centimes)}</td>
                <td className="px-4 py-3 text-green-600 border-b border-gray-100 text-right">{formatCurrency(d.inflows_centimes)}</td>
                <td className="px-4 py-3 text-red-600 border-b border-gray-100 text-right">{formatCurrency(d.outflows_centimes)}</td>
                <td className={`px-4 py-3 border-b border-gray-100 text-right font-bold ${d.closing_balance_centimes >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(d.closing_balance_centimes)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CashForecast;
